import './users-list.scss'

import styled from '@emotion/styled'
import { Fragment, useEffect, useState } from 'react'

import { UserStatus } from '../../../api/_legacy/generated-api'
import {
  DownloadLink,
  onDownloadLinkClick,
  Space,
  Tabs,
  Typography
} from '../../../components/core-v2'
import Icon from '../../../components/core-v2/atoms/Icon'
import { UserType } from '../../../types/user'
import { Counts, UserState } from '../common/users.state'
import { AddUser } from '../edit-user/add-user'
import { ManageUser } from '../edit-user/manage-user'

export type Props = {
  addNewLinkText: string
  businessUnitId?: string
  clientId?: string
  counts: Counts
  filterStatus?: UserStatus
  notifyParentOfSave: () => void
  onActivate: () => void
  onDelete: () => void
  onEdit: (userName: string) => void
  onFilterByStatus: (status: 'All' | UserStatus) => void
  onSearch: (searchTerm: string) => void
  onUnlock: () => void
  operation: 'list' | 'edit'
  selectedUser: UserState
  userType: UserType
  users: UserState[]
}

const StyledInput = styled.input`
  width: 100%;
  padding: 12px;
  margin: 0;
  border: none;
  background: var(--White95);
  font-size: 13px;
  outline: none;
  font-weight: normal;
  color: var(--Graphite);
`

export function UsersList({
  addNewLinkText,
  businessUnitId,
  clientId,
  counts,
  filterStatus,
  notifyParentOfSave,
  onActivate,
  onDelete,
  onEdit,
  onFilterByStatus,
  onSearch,
  onUnlock,
  operation,
  selectedUser,
  userType,
  users
}: Props) {
  const [isAddingNew, setIsAddingNew] = useState(false)

  useEffect(() => {
    setIsAddingNew(false)
  }, [businessUnitId])

  const filterByStatus = (status: UserStatus | 'All') => {
    setIsAddingNew(false)
    onFilterByStatus(status)
  }

  const downloadurl =
    userType === 'business-unit'
      ? `../api/usermanager/export/businessunit/${businessUnitId}`
      : `../api/usermanager/export/client/${clientId}`

  return (
    <div className="users-list">
      <Tabs.List className="mt-8 users-tab-list" spacing="md">
        <Tabs.Tab
          isSelected={filterStatus === undefined && !isAddingNew}
          value="all"
          onClick={() => filterByStatus('All')}
        >
          All ({counts.all})
        </Tabs.Tab>
        {counts.active !== 0 && (
          <Tabs.Tab
            isSelected={filterStatus === 'Active' && !isAddingNew}
            value="active"
            onClick={() => filterByStatus('Active')}
          >
            Active ({counts.active})
          </Tabs.Tab>
        )}
        {counts.dormant !== 0 && (
          <Tabs.Tab
            isSelected={filterStatus === 'Inactive' && !isAddingNew}
            value="inactive"
            onClick={() => filterByStatus('Inactive')}
          >
            Dormant ({counts.dormant})
          </Tabs.Tab>
        )}
        {counts.pending !== 0 && (
          <Tabs.Tab
            isSelected={filterStatus === 'Pending' && !isAddingNew}
            value="pending"
            onClick={() => filterByStatus('Pending')}
          >
            Pending ({counts.pending})
          </Tabs.Tab>
        )}
        {counts.locked !== 0 && (
          <Tabs.Tab
            isSelected={filterStatus === 'Locked' && !isAddingNew}
            value="locked"
            onClick={() => filterByStatus('Locked')}
          >
            Locked ({counts.locked})
          </Tabs.Tab>
        )}
        <Tabs.Tab isSelected={isAddingNew} value="add-new" onClick={() => setIsAddingNew(true)}>
          <Icon className="mr-1 add-user-circle-icon" name="circleAdd" size="medium" />
          {addNewLinkText}
        </Tabs.Tab>
      </Tabs.List>

      {isAddingNew && (
        <div>
          <AddUser
            businessUnitId={businessUnitId}
            clientId={clientId}
            notifyParentOfSave={notifyParentOfSave}
            userType={userType}
          />
        </div>
      )}

      {!isAddingNew && (
        <>
          <Space mt={6} mx={0}>
            <StyledInput
              type="text"
              onChange={e => {
                filterByStatus('All')
                onSearch(e.target.value)
              }}
            />
          </Space>

          <Space isflex justifyContent="space-between" mt={5}>
            <Typography fontSize="13px" fontWeight="800" variant="b3">
              {users.length} users found
            </Typography>
            <DownloadLink
              onClick={() =>
                onDownloadLinkClick({
                  url: downloadurl
                })
              }
            >
              Export users to csv
            </DownloadLink>
          </Space>

          <div className="list-wrapper">
            <div className="table-head">Name</div>
            <div className="table-body">
              {users.map(u => {
                const isEditing = selectedUser && selectedUser.userName === u.userName

                let editComponent
                if (isEditing) {
                  editComponent = (
                    <ManageUser
                      notifyParentOfSave={notifyParentOfSave}
                      operation={operation}
                      user={selectedUser}
                      userType={userType}
                      onActivate={onActivate}
                      onDelete={onDelete}
                      onUnlock={onUnlock}
                    />
                  )
                }

                return (
                  <Fragment key={u.userName}>
                    <div className={`row ${isEditing ? 'expanded' : ''}`}>
                      <div className="details-block">
                        <div
                          className="name"
                          role="button"
                          tabIndex={0}
                          onClick={() => onEdit(u.userName)}
                        >
                          {u.firstName} {u.lastName}
                        </div>
                        <div className={`status ${u.status}`}>{u.status}</div>
                      </div>
                      {editComponent}
                    </div>
                  </Fragment>
                )
              })}
            </div>
          </div>
        </>
      )}
    </div>
  )
}
